/* eslint react-hooks/exhaustive-deps: "warn" */

import NextLink from 'next/link';

import type { Faq as FaqType } from 'libs/http/api/content/content.types';

import { useInt } from 'hooks/useInt';

import { Faq } from 'ui/atoms/Faq/Faq';
import { Section } from 'ui/molecules/Section/Section';

import styles from './DefenceSecurityFaq.module.scss';

type Translate = (key: string) => string;

const getItems = (t: Translate): FaqType[] => [
  {
    id: 'source',
    default_opened: true,
    question: t('Where does the data come from?'),
    answer: t(
      'Directly from the government registry holding the record, across <b>400+ registries in 200+ countries</b>. Each field carries the registry, the filing reference and the retrieval timestamp, so a licensing decision made today can be reconstructed years later.',
    ),
  },
  {
    id: 'screening-list',
    question: t('We already screen against the Consolidated Screening List. Why does this matter?'),
    answer: t(
      'Because once the Affiliates Rule returns, the Consolidated Screening List stops being an exhaustive answer. <b>An entity caught by aggregate ownership appears on no list at all.</b> Establishing that requires resolved holdings from registries, which is a different product from a list match.',
    ),
  },
  {
    id: 'resolve',
    question: t('How far does ownership resolve?'),
    answer: t(
      'Through to <b>natural persons</b> wherever the jurisdiction publishes it, with each link carrying the filing it came from and the date. Where a chain runs into a jurisdiction that does not publish, the gap is shown as a gap rather than filled with an estimate.',
    ),
  },
  {
    id: 'aggregate',
    question: t('Can it calculate aggregate holdings across a chain?'),
    answer: t(
      'Holdings are returned as <b>resolved values with their sources</b>, level by level, so your own logic can apply whichever threshold and aggregation rule the relevant regime requires. We supply the evidenced ownership; the licensing determination stays with you and your counsel.',
    ),
  },
  {
    id: 'sanctions',
    question: t('Does it cover sanctions and designations?'),
    answer: t(
      'Screening runs against <b>consolidated sanctions and PEP sources</b> across the resolved group rather than the named entity alone, with the decision trail retained for audit.',
    ),
  },
  {
    id: 'integration',
    question: t('How does it reach our compliance systems?'),
    answer: t(
      'By <b>API</b> for checks at the point of a transaction, by bulk delivery for the standing supplier base, and by change feed for anything that moves. Joins are on open identifiers rather than proprietary keys.',
    ),
  },
  {
    id: 'certifications',
    question: t('What certifications do you hold?'),
    answer: t(
      "<b>ISO/IEC 27001</b> for information security management, and registration with the UK <b>Information Commissioner's Office</b>, which is checkable on the public register. The full vendor pack is released under NDA when your assessment opens.",
    ),
  },
  {
    id: 'test',
    question: t('How do we test it before committing?'),
    answer: t(
      'Send a sample of your supplier base. We return the resolved entities, the ownership chains and the gaps against what you currently hold, so <b>the evaluation runs on your own data</b> rather than a curated demo set.',
    ),
  },
];

export const DefenceSecurityFaq = () => {
  const { t } = useInt();

  return (
    <Section className={styles.root}>
      <div className={styles.head}>
        <p className={styles.lede}>{t('If yours is not here, put it to the team directly.')}</p>

        <NextLink className={styles.contact} href="/contact-us">
          <span className={styles.contactIcon}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 5.5h16v13H4z" />
              <path d="M4.5 6l7.5 6.5L19.5 6" />
            </svg>
          </span>
          <span className={styles.contactLabel}>{t('Contact the team')}</span>
          <span className={styles.contactArrow} aria-hidden="true">
            →
          </span>
        </NextLink>
      </div>

      <Faq title={t('The questions a compliance committee asks first.')} data={getItems(t)} />
    </Section>
  );
};
